import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import './components/Toast/Toast.css';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import VerifyEmail from './components/Auth/VerifyEmail';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Home from './components/Home/Home';
import Pricing from './components/Pricing/Pricing';
import TermsOfService from './components/Legal/TermsOfService';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import Disclaimer from './components/Legal/Disclaimer';
import RefundPolicy from './components/Legal/RefundPolicy';
import LogoPreview from './components/LogoPreview/LogoPreview';
import ThemeSelectorModal from './components/Common/ThemeSelectorModal';
import WelcomeModal from './components/Common/WelcomeModal';
import NotFound from './components/NotFound/NotFound';
import CookieConsent from './components/CookieConsent/CookieConsent';
import api from './utils/api';
import { trackPageView, initCtaTracking, analytics } from './utils/analytics';

// Lazy load heavy routes for code splitting
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const Trades = lazy(() => import('./components/Trades/Trades'));
const EditTrade = lazy(() => import('./components/Trades/EditTrade'));
const Positions = lazy(() => import('./components/Positions/Positions'));
const Accounts = lazy(() => import('./components/Accounts/Accounts'));
const Tools = lazy(() => import('./components/Tools/Tools'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const About = lazy(() => import('./components/About/About'));
const HowToUse = lazy(() => import('./components/HowToUse/HowToUse'));
const Blog = lazy(() => import('./components/Blog/Blog'));
const BlogPost = lazy(() => import('./components/Blog/BlogPost'));
const Calendar = lazy(() => import('./components/Calendar/Calendar'));
const FreeTools = lazy(() => import('./components/FreeTools/FreeTools'));
const OptionsProfitCalculator = lazy(() => import('./components/FreeTools/OptionsProfitCalculator'));
const WheelROICalculator = lazy(() => import('./components/FreeTools/WheelROICalculator'));
const PositionSizeCalculator = lazy(() => import('./components/FreeTools/PositionSizeCalculator'));
const Statistics = lazy(() => import('./components/Statistics/Statistics'));
const Subscription = lazy(() => import('./components/Subscription/Subscription'));

// Loading fallback component
const PageLoader = () => (
  <div className="page-wrapper">
    <div className="container">
      <div className="loading">Loading...</div>
    </div>
  </div>
);

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  // Wait for auth check to complete before redirecting
  if (loading) {
    return <div>Loading...</div>; // Or a loading spinner
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
}

// Track page views on route changes
function PageViewTracker() {
  const location = useLocation();
  const previousPathRef = useRef(null);

  // One delegated listener for signup/pricing/calculator link clicks site-wide
  useEffect(() => initCtaTracking(), []);

  useEffect(() => {
    // Get page title based on path
    const getPageTitle = (path) => {
      const titles = {
        '/': 'Home',
        '/login': 'Login',
        '/register': 'Register',
        '/verify-email': 'Verify Email',
        '/forgot-password': 'Forgot Password',
        '/reset-password': 'Reset Password',
        '/dashboard': 'Dashboard',
        '/trades': 'Trades',
        '/positions': 'Positions',
        '/calendar': 'Calendar',
        '/accounts': 'Accounts',
        '/tools': 'Tools',
        '/statistics': 'Statistics',
        '/profile': 'Profile',
        '/pricing': 'Pricing',
        '/about': 'About',
        '/how-to-use': 'How To Use',
        '/terms': 'Terms of Service',
        '/privacy': 'Privacy Policy',
        '/disclaimer': 'Disclaimer',
        '/refund-policy': 'Refund Policy',
        '/blog': 'Blog',
        '/subscription': 'Subscription',
        '/calculators': 'Free Calculators',
        '/calculators/options-profit': 'Options Profit Calculator',
        '/calculators/wheel-roi': 'Wheel Strategy ROI Calculator',
        '/calculators/position-size': 'Position Size Calculator'
      };
      if (titles[path]) return titles[path];
      // Blog posts are lazy-loaded, so document.title isn't set yet when this runs; the slug keeps
      // each post distinguishable in GA reports without pulling blogPosts.js into the main bundle.
      if (path.startsWith('/blog/')) return `Blog: ${path.slice('/blog/'.length).replace(/\/$/, '')}`;
      return 'Options Tracker';
    };

    trackPageView(location.pathname, getPageTitle(location.pathname));

    if (location.pathname === '/pricing' && previousPathRef.current !== '/pricing') {
      analytics.pricingViewed(previousPathRef.current || 'external');
    }
    previousPathRef.current = location.pathname;
  }, [location]);

  return null;
}

// Show theme selection modal for first-time authenticated users
function FirstTimeThemeSelector() {
  const { isAuthenticated, loading } = useAuth();
  const { hasSelectedTheme } = useTheme();

  // Don't show modal while auth is still loading
  if (loading) return null;

  // Show modal only for authenticated users who haven't selected a theme
  return <ThemeSelectorModal open={isAuthenticated && !hasSelectedTheme} />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/logo-preview" element={<PrivateRoute><LogoPreview /></PrivateRoute>} />
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/calculators" element={<FreeTools />} />
        <Route path="/calculators/options-profit" element={<OptionsProfitCalculator />} />
        <Route path="/calculators/wheel-roi" element={<WheelROICalculator />} />
        <Route path="/calculators/position-size" element={<PositionSizeCalculator />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/trades"
          element={
            <PrivateRoute>
              <Trades />
            </PrivateRoute>
          }
        />
        <Route
          path="/trades/:id/edit"
          element={
            <PrivateRoute>
              <EditTrade />
            </PrivateRoute>
          }
        />
        <Route
          path="/positions"
          element={
            <PrivateRoute>
              <Positions />
            </PrivateRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <PrivateRoute>
              <Calendar />
            </PrivateRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <PrivateRoute>
              <Accounts />
            </PrivateRoute>
          }
        />
        <Route
          path="/tools"
          element={
            <PrivateRoute>
              <Tools />
            </PrivateRoute>
          }
        />
        <Route
          path="/statistics"
          element={
            <PrivateRoute>
              <Statistics />
            </PrivateRoute>
          }
        />
        <Route
          path="/about"
          element={
            <PrivateRoute>
              <About />
            </PrivateRoute>
          }
        />
        <Route
          path="/how-to-use"
          element={
            <PrivateRoute>
              <HowToUse />
            </PrivateRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <PrivateRoute>
              <Subscription />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Keep-alive mechanism to prevent Render free tier spin-down
  useEffect(() => {
    // Only run keep-alive in production (when using Render)
    if (process.env.NODE_ENV === 'production') {
      const keepAliveInterval = setInterval(() => {
        // Ping the backend every 5 minutes to keep it awake (Render spins down after 15 min)
        // More frequent pings ensure the instance stays awake even if one ping fails
        api.get('/ping').catch(() => {
          // Silently fail - this is just a keep-alive, don't show errors
        });
      }, 5 * 60 * 1000); // 5 minutes (more frequent to ensure reliability)

      // Also ping immediately when app loads
      api.get('/ping').catch(() => {});

      return () => clearInterval(keepAliveInterval);
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <Router>
              <PageViewTracker />
              <FirstTimeThemeSelector />
              <WelcomeModal />
              <CookieConsent />
              <div className="App">
                <AppRoutes />
              </div>
            </Router>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;

